import { addDays, endOfDay, format, startOfDay, subDays, subMonths, subYears } from 'date-fns';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Button, DateRange, DateRangeOption, DateRangeSelector, HStack, rem, Text, VStack } from '@bonusly/design-system';

import { Award, GetIncentivesSummaryParams } from '../hooks/use-incentives';
import { defaultParams } from './incentives-table';
import { NameFilter } from './name-filter';

interface TableFilterProps {
	awards: Award[];
	params: GetIncentivesSummaryParams;
	setParams: (params: GetIncentivesSummaryParams) => void;
}

const TableFilters: FC<TableFilterProps> = ({ awards, params, setParams }) => {
	const { t } = useTranslation('admin', {
		keyPrefix: 'incentives.summary.filters',
	});
	const { track } = useAnalytics();

	const onClearAllClick = () => {
		track(AnalyticsEvent.AdminIncentives.SummaryClearAll);

		setParams({
			...params,
			page_number: defaultParams.page_number,
			award_ids: defaultParams.award_ids,
			end_date: defaultParams.end_date,
			start_date: defaultParams.start_date,
		});
	};

	const handleChange = (dates: DateRange) => {
		if (dates[0] && dates[1]) {
			track(AnalyticsEvent.AdminIncentives.SummaryTimeframeChange, {
				startDate: format(dates[0], 'yyyy-MM-dd'),
				endDate: format(dates[1], 'yyyy-MM-dd'),
			});

			setParams({
				...params,
				// NOTE: reset to page 1 when changing date filter
				page_number: 1,
				start_date: startOfDay(dates[0]).toISOString(),
				end_date: endOfDay(dates[1]).toISOString(),
			});
		}
	};

	return (
		<HStack p={4} spacing={2}>
			<VStack spacing={1} alignItems="left" w="fit-content">
				<Text fontSize={50} fontWeight="medium" lineHeight={5}>
					{t('timeframe')}
				</Text>

				<DateRangeSelector isClearable={false} onChange={handleChange} value={[new Date(params.start_date), new Date(params.end_date)]} w={rem(300)}>
					<DateRangeOption startDate={startOfDay(subDays(new Date(), 6))} endDate={endOfDay(new Date())}>
						{t('last_days', { days: 7 })}
					</DateRangeOption>

					<DateRangeOption startDate={startOfDay(subDays(new Date(), 29))} endDate={endOfDay(new Date())}>
						{t('last_days', { days: 30 })}
					</DateRangeOption>

					<DateRangeOption startDate={startOfDay(subDays(new Date(), 89))} endDate={endOfDay(new Date())}>
						{t('last_days', { days: 90 })}
					</DateRangeOption>

					<DateRangeOption startDate={startOfDay(subMonths(new Date(), 6))} endDate={endOfDay(new Date())}>
						{t('last_months', { months: 6 })}
					</DateRangeOption>

					<DateRangeOption startDate={startOfDay(addDays(subYears(new Date(), 1), 1))} endDate={endOfDay(new Date())}>
						{t('last_year')}
					</DateRangeOption>
				</DateRangeSelector>
			</VStack>
			<VStack spacing={1} alignItems="left" w="fit-content">
				<Text fontSize={50} fontWeight="medium" lineHeight={5}>
					{t('name')}
				</Text>
				<NameFilter awards={awards} params={params} setParams={setParams} />
			</VStack>
			<Button ml={2} top={3} variant="link" onClick={onClearAllClick}>
				{t('clear_all')}
			</Button>
		</HStack>
	);
};

export { TableFilters };
